import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Landmark, Globe, BarChart3, FileText, Users, 
  TrendingUp, Building2, Scale, Network, Cpu, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getToolsByLab } from "@/data/aiRegistry";
import BackNavigation from "@/components/BackNavigation";

/**
 * Institutional Lab Page
 * Sector and system-level workforce intelligence
 * Policy frameworks, benchmarking, regional intelligence
 */
export default function InstitutionalLab() {
  const capabilities = [
    {
      icon: Globe,
      title: "Sector-Level Intelligence",
      description: "Analyze workforce intelligence patterns and trends across entire industry sectors."
    },
    {
      icon: BarChart3,
      title: "Workforce System Analysis",
      description: "Evaluate workforce development systems at regional and national levels."
    },
    {
      icon: Scale,
      title: "Policy & Framework Support",
      description: "Develop evidence-based policies and frameworks for workforce development."
    },
    {
      icon: TrendingUp,
      title: "Industry Benchmarking",
      description: "Establish and track industry-wide workforce intelligence benchmarks."
    },
    {
      icon: Network,
      title: "Regional Workforce Intelligence",
      description: "Map and analyze workforce intelligence across geographic regions."
    },
    {
      icon: FileText,
      title: "Research & Publications",
      description: "Generate sector-specific research, reports, and thought leadership."
    }
  ];

  const stakeholders = [
    { name: "Government Agencies", desc: "Workforce policy and planning" },
    { name: "Industry Associations", desc: "Sector-wide standards and benchmarks" },
    { name: "Educational Institutions", desc: "Curriculum alignment with workforce needs" },
    { name: "Economic Development Orgs", desc: "Regional workforce strategies" }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="institutional-lab-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <Link to="/zodiac-lab" className="text-sm text-[#1E3A8A]/60 hover:text-[#1E3A8A]">
                Zodiac Lab
              </Link>
              <span className="text-[#1E3A8A]/40">/</span>
              <span className="text-sm text-[#1E3A8A] font-medium">Institutional Lab</span>
            </div>

            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3B82F6]/10 text-[#3B82F6] text-sm font-medium mb-6">
              <Landmark className="w-4 h-4" />
              System Intelligence
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
              Institutional Lab
            </h1>
            <p className="text-xl font-semibold text-[#1E3A8A] mb-4">
              Sector and System-Level Workforce Intelligence
            </p>
            <p className="text-base text-[#1E3A8A]/80 leading-relaxed mb-8 max-w-3xl">
              Institutional Lab applies WAIT Theory at the sector, industry, and policy level. 
              Support workforce development systems, inform policy decisions, and drive sector-wide 
              transformation through rigorous intelligence and analysis.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-[#3B82F6] text-white hover:bg-[#2563EB]">
                  Partner With Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/research/reports">
                <Button size="lg" variant="outline" className="border-[#991B1B] text-[#991B1B] hover:bg-[#991B1B] hover:text-white">
                  View Research
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Institutional Capabilities
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Tools and services for sector-wide workforce intelligence transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-4">
                      <capability.icon className="w-6 h-6 text-[#3B82F6]" />
                    </div>
                    <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                      {capability.title}
                    </h3>
                    <p className="text-sm text-[#1E3A8A]/80">{capability.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakeholders */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Who We Serve
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Institutional Lab partners with organizations shaping workforce systems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stakeholders.map((stakeholder, index) => (
              <motion.div
                key={stakeholder.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white text-center">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center mx-auto mb-4">
                      <Building2 className="w-6 h-6 text-[#3B82F6]" />
                    </div>
                    <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                      {stakeholder.name}
                    </h3>
                    <p className="text-sm text-[#1E3A8A]/70">{stakeholder.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lab AI Tool Library Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#991B1B' }}>
                Lab AI Tool Library
              </h2>
              <p className="text-lg text-[#1E3A8A]">
                AI tools curated for institutional and sector-level intelligence.
              </p>
            </div>
            <Link to="/observatories/ai/registry?lab=institutional">
              <Button variant="outline" className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white">
                View All {getToolsByLab('institutional').length} Tools
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getToolsByLab('institutional').slice(0, 4).map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-md hover:border-[#3B82F6]/30 transition-all">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-[#3B82F6]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#991B1B] truncate">{tool.name}</h4>
                        <p className="text-xs text-[#1E3A8A]/60">{tool.provider}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#1E3A8A]/70 mb-3 line-clamp-2">
                      {tool.labRelevance.institutional.useCases[0]}
                    </p>
                    <a 
                      href={tool.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#3B82F6] hover:underline"
                    >
                      Learn more <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#3B82F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Partner for System-Level Impact
          </h2>
          <p className="text-white/80 mb-8">
            Work with Institutional Lab to transform workforce systems at scale.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#3B82F6] hover:bg-white/90 h-14 px-8">
              Contact Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
